import React from "react";
import {
  Autocomplete as MUIAutoComplete,
  Tooltip,
  Chip,
} from "@mui/material";
import { CustomTextField } from "../formsComponents";

export interface AutoCompleteOption {
  label: string;
  value: string;
}

export interface AutoCompleteProps {
  value: string | string[] | null | any;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  options: AutoCompleteOption[];
  fullWidth?: boolean;
  error?: boolean;
  onChange?: (value: string | string[] | null | any) => void;
  multiple?: boolean;
  maxVisibleChips?: number; // ✅ manual prop to control visible chip count
}

export const AutoComplete: React.FC<AutoCompleteProps> = (props) => {
  const {
    value,
    placeholder,
    helperText,
    options,
    onChange,
    disabled = false,
    fullWidth,
    error = false,
    multiple = false,
    maxVisibleChips = 2, // ✅ show first 2 chips by default
  } = props;

  // ✅ Normalize value type
  const normalizedValue = React.useMemo(() => {
    if (multiple) {
      if (!value) return [];
      const arrayVal = Array.isArray(value) ? value : [value];
      return options.filter((opt) =>
        arrayVal.some(
          (val) =>
            typeof val === "string" &&
            val.toLowerCase() === opt.value.toLowerCase()
        )
      );
    } else {
      if (typeof value === "string" && value) {
        return (
          options.find(
            (x) => x.value.toLowerCase() === value.toLowerCase()
          ) || null
        );
      }
      return null;
    }
  }, [value, options, multiple]);

  // ✅ Handle selection changes
  const handleChange = (event: any, newValue: any) => {
    if (!onChange) return;

    if (multiple) {
      const selectedValues = (newValue || []).map(
        (v: AutoCompleteOption) => v.value
      );
      onChange(selectedValues);
    } else {
      onChange(newValue ? newValue.value : null);
    }
  };

  return (
    <MUIAutoComplete
      multiple={multiple}
      sx={{
        width: fullWidth ? "100%" : "auto",
        ".MuiOutlinedInput-root": {
          paddingTop: "0px",
          paddingBottom: "0px",
          color: "rgb(38, 38, 38)",
          width: "100%",
        },
      }}
      key={options.length}
      value={normalizedValue}
      options={options}
      autoHighlight
      getOptionLabel={(option) => option.label}
      onChange={handleChange}
      disabled={disabled}
      renderTags={(tagValue, getTagProps) => {
        if (!multiple) return null;

        const visibleTags = tagValue.slice(0, maxVisibleChips);
        const hiddenTags = tagValue.slice(maxVisibleChips);

        return (
          <>
            {visibleTags.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                size="small"
              />
            ))}

            {hiddenTags.length > 0 && (
              <Tooltip
                title={hiddenTags.map((opt) => opt.label).join(", ")}
                arrow
              >
                <Chip
                  label={`+${hiddenTags.length} more`}
                  size="small"
                  sx={{ cursor: "pointer" }}
                />
              </Tooltip>
            )}
          </>
        );
      }}
      renderInput={(params) => (
        <CustomTextField
          {...params}
          fullWidth
          placeholder={placeholder}
          disabled={disabled}
          helperText={helperText}
          error={error}
          InputProps={{
            ...params.InputProps,
          }}
        />
      )}
    />
  );
};
